import React from "react";
import { observer } from "mobx-react-lite";
import { Hooks } from "@xt/xtbase";
import step6Img from "@/assets/img/cfd-guide/guide1.png";
import lineImg from "@/assets/img/cfd-guide/line.png";
import hoveringImg from "@/assets/img/hovering.png";
import { useCfdGuideStageRect } from "./useCfdGuideStageRect";
import { useCfdGuideAnchoredTipLayout } from "./useCfdGuideAnchoredTipLayout";
import styles from "./ProGuideStep6.module.scss";

const { useTranslation } = Hooks;

export type ProGuideStep6Props = {
  onSkip: () => void;
  onNext: () => void;
};

const DESIGN_WIDTH = 375;

function getScale(): number {
  if (typeof window === "undefined") return 1;
  const w = window.innerWidth || DESIGN_WIDTH;
  return w / DESIGN_WIDTH;
}

const ProGuideStep6: React.FC<ProGuideStep6Props> = ({ onSkip, onNext }) => {
  const t = useTranslation();

  const scale = getScale();
  const anchor = useCfdGuideStageRect('[data-guide-target="proQuantityRow"]');
  const tipLayout = useCfdGuideAnchoredTipLayout(anchor, scale);

  const imgSrc = (step6Img as any)?.src || (step6Img as any);
  const lineImgSrc = (lineImg as any)?.src || (lineImg as any);
  const hoveringImgSrc = (hoveringImg as any)?.src || (hoveringImg as any);

  return (
    <div style={{ ["--sg-scale" as any]: scale }}>
      {anchor && tipLayout ? (
        <>
          <div
            className={styles.highlightRect}
            style={{
              position: "fixed",
              left: anchor.left,
              top: anchor.top,
              width: anchor.width,
              height: anchor.height,
              zIndex: 10001,
            }}
          >
            <div className={styles.content}>
              <div className={styles.amountInput}>
                <div className={styles.inputLeft}>
                  <div className={styles.iconBtn}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 7H11" stroke="#707a8a" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className={styles.inputText}>{t("cfd.amount")}</div>
                  <div className={styles.iconBtn}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M6 2V10M2 6H10" stroke="#707a8a" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>
                <div className={styles.inputRight}>
                  <span className={styles.currencyText}>USDT</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
                    <path d="M10 0H0L5 6L10 0Z" fill="rgba(255, 255, 255, 0.56)" />
                  </svg>
                </div>
              </div>
              <div className={styles.slider}>
                <img className={styles.sliderImg} src={hoveringImgSrc} alt="" />
              </div>
            </div>
          </div>

          <div
            className={styles.tip}
            style={{
              position: "fixed",
              left: tipLayout.tipLeft,
              top: anchor.top + anchor.height + tipLayout.gapBelow,
              width: tipLayout.tipWidth,
              zIndex: 10001,
            }}
          >
            <div className={styles.tipBg}>
              <div className={styles.tipText}>
                <div className={styles.tipTitle}>{t("cfd.guide.pro.step6.title")}</div>
                <div className={styles.tipDesc}>{t("cfd.guide.pro.step6.desc")}</div>
              </div>
              <img className={styles.tipImg} src={imgSrc} alt="" />
            </div>
            <div className={styles.star1} />
            <div className={styles.star2} />
            <img
              className={styles.lineImg}
              src={lineImgSrc}
              alt=""
              style={{
                left: tipLayout.lineCenterInTip,
                transform: "translateX(-50%)",
              }}
            />
          </div>
        </>
      ) : null}

      <div className={styles.btnGroup}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onNext}>
          {`${t("cfd.guide.next")} 6/9`}
        </button>
        <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onSkip}>
          {t("cfd.guide.skip")}
        </button>
      </div>
    </div>
  );
};

export default observer(ProGuideStep6);
